import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { eventsApi, AgentEvent } from '@/services/api/agentApi';
import * as errorUtils from '@/utils/errors';

interface EventFilters {
  category?: string;
  tier?: string;
  minScore?: number;
  since?: string;
  until?: string;
  community_id?: string;
}

interface EventsStoreState {
  events: AgentEvent[];
  activeEvents: AgentEvent[];
  total: number;
  filters: EventFilters;
  isLoading: boolean;
  error: string | null;
}

interface EventsStoreActions {
  fetchEvents: (params?: Record<string, string>) => Promise<void>;
  fetchActiveEvents: () => Promise<void>;
  setFilters: (filters: EventFilters) => void;
}

export const useEventsStore = create<EventsStoreState & EventsStoreActions>()(
  immer((set, get) => ({
    events: [],
    activeEvents: [],
    total: 0,
    filters: {},
    isLoading: false,
    error: null,

    fetchEvents: async (params) => {
      set((s) => { s.isLoading = true; s.error = null; });
      try {
        const filters = get().filters;
        const queryParams: Record<string, string> = { ...params };
        if (filters.category) queryParams.category = filters.category;
        if (filters.tier) queryParams.tier = filters.tier;
        if (filters.minScore !== undefined) queryParams.minScore = String(filters.minScore);
        if (filters.since) queryParams.since = filters.since;
        if (filters.until) queryParams.until = filters.until;
        if (filters.community_id) queryParams.community_id = filters.community_id;

        const result = await eventsApi.list(queryParams);
        set((s) => {
          s.events = result.data;
          s.total = result.total || result.data.length;
          s.isLoading = false;
        });
      } catch (error: unknown) {
        set((s) => { s.error = errorUtils.getErrorMessageOrFallback(error, 'Failed to fetch events'); s.isLoading = false; });
      }
    },

    fetchActiveEvents: async () => {
      try {
        const result = await eventsApi.getActive();
        set((s) => { s.activeEvents = result.data; });
      } catch (error: unknown) {
        set((s) => { s.error = errorUtils.getErrorMessage(error); });
      }
    },

    setFilters: (filters) => set((s) => { s.filters = filters; }),
  }))
);
